/**
 * Simplify Results - Transform technical scan findings into plain language
 * for non-technical business users
 */

import type { ScanResult, ScoredFinding, RiskLevel, FindingType } from "./api";

// ============================================================================
// TYPES
// ============================================================================

export type BusinessCategory =
  | "email"      // SPF, DKIM, DMARC
  | "website"    // TLS, Headers
  | "domain"     // WHOIS, DNS
  | "exposure"   // Ports, Cloud, Repos
  | "brand";     // Typosquat

export interface SimplifiedScore {
  /** Overall risk level derived from findings */
  riskLevel: RiskLevel;
  /** Human-readable assessment based on risk level */
  assessment: string;
  /** Total number of findings */
  findingCount: number;
  /** Count of urgent items (critical + high) */
  urgentCount: number;
}

export interface PriorityAction {
  id: string;
  /** Priority ranking (1 = highest) */
  priority: number;
  /** Short headline - what to do */
  title: string;
  /** Plain language explanation of the issue */
  description: string;
  /** Category for grouping */
  category: BusinessCategory;
  /** Severity for styling */
  severity: RiskLevel;
  /** What type of finding this is */
  findingType: FindingType;
  /** Link to learn more (optional) */
  learnMoreUrl?: string;
  /** Original technical finding for reference */
  originalFinding?: ScoredFinding;
}

export interface CategorySummary {
  category: BusinessCategory;
  label: string;
  icon: string; // Icon name for the category
  status: "good" | "warning" | "critical";
  issueCount: number;
  /** Brief status description */
  statusText: string;
}

export interface RiskDistribution {
  critical: number;
  high: number;
  medium: number;
  low: number;
  info: number;
}

export interface SimplifiedResults {
  /** Overall risk assessment */
  score: SimplifiedScore;
  /** Top priority actions (3-5 items) */
  priorityActions: PriorityAction[];
  /** Category-level summaries */
  categories: CategorySummary[];
  /** Whether to show risk-based view (has critical/high/medium) or category view */
  showRiskBasedView: boolean;
  /** Total finding count */
  totalFindings: number;
  /** Count of urgent items (critical + high) */
  urgentCount: number;
  /** Distribution of findings by severity */
  distribution: RiskDistribution;
}

// ============================================================================
// TRANSLATIONS - Technical to Plain Language
// ============================================================================

/**
 * Maps technical finding types to plain language explanations
 */
const FINDING_TRANSLATIONS: Record<FindingType, {
  title: string;
  description: string;
  category: BusinessCategory;
  learnMoreUrl?: string;
}> = {
  // Certificate issues
  certificate_expired: {
    title: "Your security certificate has expired",
    description: "Visitors will see scary warning messages and may not trust your website.",
    category: "website",
    learnMoreUrl: "https://letsencrypt.org/",
  },
  certificate_expiring_soon: {
    title: "Your security certificate expires soon",
    description: "Renew it now to avoid your website showing security warnings to visitors.",
    category: "website",
    learnMoreUrl: "https://letsencrypt.org/",
  },
  certificate_weak_signature: {
    title: "Your certificate uses outdated security",
    description: "Modern browsers may flag this as insecure. Consider getting a new certificate.",
    category: "website",
  },
  certificate_self_signed: {
    title: "Your certificate isn't trusted by browsers",
    description: "Visitors see a warning because your certificate isn't from a recognized authority.",
    category: "website",
    learnMoreUrl: "https://letsencrypt.org/",
  },
  certificate_hostname_mismatch: {
    title: "Certificate doesn't match your domain",
    description: "Your security certificate was issued for a different website name.",
    category: "website",
  },
  certificate_chain_incomplete: {
    title: "Certificate setup is incomplete",
    description: "Some visitors may see security warnings due to missing certificate information.",
    category: "website",
  },

  // DNS issues
  dns_missing_ns: {
    title: "DNS configuration needs attention",
    description: "Your domain's name servers aren't properly configured.",
    category: "domain",
  },
  dns_dangling_cname: {
    title: "Unused DNS record found",
    description: "A DNS record points to a service you no longer use, which could be hijacked.",
    category: "domain",
  },
  dns_zone_transfer_enabled: {
    title: "DNS zone transfer is exposed",
    description: "Attackers can download your entire DNS configuration.",
    category: "domain",
  },
  dns_no_sec_dnssec: {
    title: "DNS security extension not enabled",
    description: "DNSSEC adds extra protection but isn't set up on your domain.",
    category: "domain",
  },

  // Email security
  spf_missing: {
    title: "Email sender verification not set up",
    description: "Others can easily send emails that look like they're from your domain.",
    category: "email",
    learnMoreUrl: "https://www.dmarcanalyzer.com/spf/",
  },
  spf_too_permissive: {
    title: "Email sender rules are too loose",
    description: "Your email security allows too many servers to send on your behalf.",
    category: "email",
  },
  dkim_missing: {
    title: "Email signature verification not enabled",
    description: "Recipients can't verify that emails from you haven't been tampered with.",
    category: "email",
    learnMoreUrl: "https://www.dmarcanalyzer.com/dkim/",
  },
  dmarc_missing: {
    title: "Enable email spoofing protection",
    description: "Anyone can send emails pretending to be you. This is a common attack vector.",
    category: "email",
    learnMoreUrl: "https://dmarc.org/",
  },
  dmarc_none: {
    title: "Email spoofing protection is in monitor-only mode",
    description: "You're tracking spoofed emails but not blocking them yet.",
    category: "email",
    learnMoreUrl: "https://dmarc.org/",
  },
  dmarc_quarantine: {
    title: "Spoofed emails go to spam, but aren't rejected",
    description: "Consider upgrading to full rejection for better protection.",
    category: "email",
  },

  // HTTP header issues
  headers_missing_hsts: {
    title: "Force secure connections for visitors",
    description: "Visitors could be tricked into using insecure connections to your site.",
    category: "website",
    learnMoreUrl: "https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Strict-Transport-Security",
  },
  headers_missing_csp: {
    title: "Add content security policy",
    description: "Your website is more vulnerable to code injection attacks.",
    category: "website",
    learnMoreUrl: "https://developer.mozilla.org/en-US/docs/Web/HTTP/CSP",
  },
  headers_missing_xfo: {
    title: "Prevent your site from being embedded",
    description: "Attackers could display your site in a frame for clickjacking attacks.",
    category: "website",
  },
  headers_missing_xcto: {
    title: "Fix content type security header",
    description: "Browsers might misinterpret file types, creating security risks.",
    category: "website",
  },
  headers_server_info_leak: {
    title: "Hide your server information",
    description: "Your server is revealing its software version, helping attackers find vulnerabilities.",
    category: "website",
  },
  headers_powered_by_leak: {
    title: "Remove technology disclosure",
    description: "Your site reveals what technology powers it, helping attackers target known issues.",
    category: "website",
  },

  // TLS issues
  tls_weak_cipher: {
    title: "Upgrade encryption strength",
    description: "Your site uses outdated encryption that could be broken by attackers.",
    category: "website",
  },
  tls_old_protocol: {
    title: "Disable old security protocols",
    description: "Your site supports outdated protocols that have known security flaws.",
    category: "website",
  },
  tls_missing_pfs: {
    title: "Enable forward secrecy",
    description: "If your encryption key is ever compromised, past communications could be decrypted.",
    category: "website",
  },

  // Exposure issues
  exposure_open_port: {
    title: "Network service is publicly visible",
    description: "A service on your network can be accessed from the internet.",
    category: "exposure",
  },
  exposure_sensitive_port: {
    title: "Sensitive service is exposed",
    description: "A service that should be private (like a database) is accessible from the internet.",
    category: "exposure",
  },
  exposure_known_vulnerability: {
    title: "Known security vulnerability detected",
    description: "A service you're running has a publicly known security flaw.",
    category: "exposure",
  },
  exposure_credential_leak: {
    title: "Credentials found in public sources",
    description: "Usernames or passwords associated with your organization were found online.",
    category: "exposure",
  },
  exposure_data_breach: {
    title: "Organization data found in breach",
    description: "Your organization's data appeared in a known data breach.",
    category: "exposure",
  },

  // Cloud issues
  cloud_public_bucket: {
    title: "Cloud storage is publicly accessible",
    description: "Files in your cloud storage can be viewed by anyone on the internet.",
    category: "exposure",
  },
  cloud_misconfigured_iam: {
    title: "Cloud permissions need review",
    description: "Cloud access controls may allow more access than intended.",
    category: "exposure",
  },
  cloud_open_security_group: {
    title: "Cloud firewall is too open",
    description: "Your cloud security settings allow more network access than recommended.",
    category: "exposure",
  },

  // Repository issues
  repo_secrets_exposed: {
    title: "Secrets found in code repository",
    description: "API keys, passwords, or other secrets are visible in your code.",
    category: "exposure",
  },
  repo_sensitive_files: {
    title: "Sensitive files in code repository",
    description: "Configuration files with sensitive information are publicly visible.",
    category: "exposure",
  },

  // Tech issues
  tech_outdated_software: {
    title: "Software is out of date",
    description: "You're using an older version that may have security vulnerabilities.",
    category: "website",
  },
  tech_end_of_life: {
    title: "Software is no longer supported",
    description: "This software no longer receives security updates.",
    category: "website",
  },

  // Fallback
  unknown: {
    title: "Security issue detected",
    description: "A potential security concern was identified that needs review.",
    category: "exposure",
  },
};

// Category metadata
const CATEGORY_META: Record<BusinessCategory, { label: string; icon: string }> = {
  email: { label: "Email Security", icon: "Mail" },
  website: { label: "Website Security", icon: "Globe" },
  domain: { label: "Domain Health", icon: "Shield" },
  exposure: { label: "External Exposure", icon: "Eye" },
  brand: { label: "Brand Protection", icon: "AlertOctagon" },
};

// ============================================================================
// RISK LEVEL FUNCTIONS
// ============================================================================

/**
 * Get overall risk level from finding severity distribution
 *
 * This derives risk from actual findings rather than arbitrary score thresholds,
 * ensuring consistency between Simple and Advanced views.
 *
 * Logic: The overall risk level is the highest severity present in findings.
 * - Any critical finding → Critical risk
 * - Any high finding → High risk
 * - Any medium finding → Medium risk
 * - Only low findings → Low risk
 * - No findings → Info (excellent)
 */
function getRiskLevelFromDistribution(distribution: {
  critical: number;
  high: number;
  medium: number;
  low: number;
  info: number;
}): RiskLevel {
  if (distribution.critical > 0) return "critical";
  if (distribution.high > 0) return "high";
  if (distribution.medium > 0) return "medium";
  if (distribution.low > 0) return "low";
  return "info";
}

/**
 * Generate human-readable assessment based on risk level
 * Uses risk level (derived from findings) rather than arbitrary score thresholds
 */
function getAssessment(riskLevel: RiskLevel, findingCount: number): string {
  switch (riskLevel) {
    case "critical":
      return "Critical security issues require immediate attention.";
    case "high":
      return "Significant security issues should be addressed soon.";
    case "medium":
      return "Security improvements are recommended.";
    case "low":
      return "Your security is good with minor improvements possible.";
    case "info":
    default:
      return findingCount === 0
        ? "No security issues detected."
        : "Your security posture is excellent.";
  }
}

// ============================================================================
// SEVERITY ADJUSTMENT
// ============================================================================

/**
 * Finding types that should be floored to "high" severity.
 * These represent significant security exposures that justify at least high,
 * but the backend score can still push them to critical if warranted.
 */
const HIGH_SEVERITY_FINDINGS: Set<FindingType> = new Set([
  "certificate_expired",
  "cloud_public_bucket",
  "cloud_open_security_group",
  "repo_secrets_exposed",
  "exposure_sensitive_port",
  "exposure_credential_leak",
  "exposure_data_breach",
]);

/**
 * Finding types that are unconditionally critical regardless of score.
 * Only include findings where active exploitation or total data loss is certain.
 * Currently empty — severity escalation to critical is signal-driven (KEV, CVSS 9+).
 */
const UNCONDITIONAL_CRITICAL_FINDINGS: Set<FindingType> = new Set([]);

/**
 * Adjust finding severity based on finding type and CVE signals.
 *
 * Rules (evaluated in order):
 * 1. Unconditionally critical types → critical
 * 2. Known vulnerabilities: trust the backend CVSS score, but escalate to
 *    critical if the CVE is a KEV (actively exploited in the wild) regardless
 *    of score
 * 3. High-floor types: never show below high, but allow backend to push higher
 * 4. Everything else: use backend score as-is
 */
function adjustFindingSeverity(finding: ScoredFinding): RiskLevel {
  const originalSeverity = finding.score.risk_level;

  if (UNCONDITIONAL_CRITICAL_FINDINGS.has(finding.finding_type)) {
    return "critical";
  }

  if (finding.finding_type === "exposure_known_vulnerability") {
    // KEV = actively exploited in the wild → always critical
    if (finding.is_kev) return "critical";
    // Otherwise trust the backend's CVSS-based severity
    return originalSeverity;
  }

  if (HIGH_SEVERITY_FINDINGS.has(finding.finding_type)) {
    // Floor at high, but keep critical if backend already scored it there
    return originalSeverity === "critical" ? "critical" : "high";
  }

  return originalSeverity;
}

/**
 * Recalculate distribution with adjusted severities
 */
function calculateAdjustedDistribution(findings: ScoredFinding[]): RiskDistribution {
  const distribution: RiskDistribution = {
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
    info: 0,
  };

  for (const finding of findings) {
    const adjustedSeverity = adjustFindingSeverity(finding);
    distribution[adjustedSeverity]++;
  }

  return distribution;
}

// ============================================================================
// MAIN TRANSFORMATION FUNCTION
// ============================================================================

/**
 * Transform technical scan results into simplified, business-friendly format
 */
export function simplifyResults(results: ScanResult): SimplifiedResults {
  const { ai_insights, modules } = results;

  // If no AI insights, create basic summary from modules
  if (!ai_insights) {
    return createBasicSimplifiedResults(modules);
  }

  const { findings, summary } = ai_insights;

  // Recalculate distribution with adjusted severities for consistency
  const adjustedDistribution = calculateAdjustedDistribution(findings);

  // Derive risk level from adjusted distribution
  const riskLevel = getRiskLevelFromDistribution(adjustedDistribution);

  // Count urgent findings (with adjusted severity)
  const urgentCount = adjustedDistribution.critical + adjustedDistribution.high;

  // Determine if we should show risk-based view (using adjusted distribution)
  const hasSignificantRisks =
    adjustedDistribution.critical > 0 ||
    adjustedDistribution.high > 0 ||
    adjustedDistribution.medium > 0;

  // Transform findings to priority actions (with adjusted severity)
  const priorityActions = transformToPriorityActions(findings, true);

  // Build category summaries
  const categories = buildCategorySummaries(findings, modules);

  return {
    score: {
      riskLevel,
      assessment: getAssessment(riskLevel, summary.total_findings),
      findingCount: summary.total_findings,
      urgentCount,
    },
    priorityActions: priorityActions.slice(0, 5), // Top 5 actions
    categories,
    showRiskBasedView: hasSignificantRisks,
    totalFindings: summary.total_findings,
    urgentCount,
    distribution: adjustedDistribution,
  };
}

/**
 * Transform scored findings into priority actions
 */
function transformToPriorityActions(findings: ScoredFinding[], useAdjustedSeverity = false): PriorityAction[] {
  // Sort by rank (already scored and ranked by backend)
  const sorted = [...findings].sort((a, b) => a.rank - b.rank);

  return sorted.map((finding, index) => {
    const translation = FINDING_TRANSLATIONS[finding.finding_type] || FINDING_TRANSLATIONS.unknown;
    const severity = useAdjustedSeverity
      ? adjustFindingSeverity(finding)
      : finding.score.risk_level;

    return {
      id: finding.id,
      priority: index + 1,
      title: translation.title,
      description: translation.description,
      category: translation.category,
      severity,
      findingType: finding.finding_type,
      learnMoreUrl: translation.learnMoreUrl,
      originalFinding: finding,
    };
  });
}

/**
 * Build category summaries from findings
 */
function buildCategorySummaries(
  findings: ScoredFinding[],
  modules: Record<string, unknown>
): CategorySummary[] {
  // Group findings by category
  const categoryFindings: Record<BusinessCategory, ScoredFinding[]> = {
    email: [],
    website: [],
    domain: [],
    exposure: [],
    brand: [],
  };

  for (const finding of findings) {
    const translation = FINDING_TRANSLATIONS[finding.finding_type] || FINDING_TRANSLATIONS.unknown;
    categoryFindings[translation.category].push(finding);
  }

  // Also check modules for basic presence (even without findings)
  const moduleCategories: Record<string, BusinessCategory> = {
    email: "email",
    dns: "domain",
    whois: "domain",
    tls: "website",
    headers: "website",
    tech: "website",
    cloud: "exposure",
    repos: "exposure",
    portscan: "exposure",
    ipintel: "exposure",
    typosquat: "brand",
  };

  const presentCategories = new Set<BusinessCategory>();
  for (const moduleName of Object.keys(modules)) {
    const cat = moduleCategories[moduleName];
    if (cat) presentCategories.add(cat);
  }

  // Build summaries
  const summaries: CategorySummary[] = [];

  for (const category of ["email", "website", "domain", "exposure", "brand"] as BusinessCategory[]) {
    const categoryFindingsList = categoryFindings[category];
    const hasData = presentCategories.has(category) || categoryFindingsList.length > 0;

    if (!hasData) continue;

    const meta = CATEGORY_META[category];
    const issueCount = categoryFindingsList.length;

    // Determine status based on severity of findings
    let status: "good" | "warning" | "critical" = "good";
    let statusText = "No issues found";

    if (categoryFindingsList.some(f => f.score.risk_level === "critical")) {
      status = "critical";
      statusText = `${issueCount} critical ${issueCount === 1 ? "issue" : "issues"}`;
    } else if (categoryFindingsList.some(f =>
      f.score.risk_level === "high" || f.score.risk_level === "medium"
    )) {
      status = "warning";
      statusText = `${issueCount} ${issueCount === 1 ? "issue" : "issues"} found`;
    } else if (issueCount > 0) {
      statusText = `${issueCount} minor ${issueCount === 1 ? "item" : "items"}`;
    }

    summaries.push({
      category,
      label: meta.label,
      icon: meta.icon,
      status,
      issueCount,
      statusText,
    });
  }

  return summaries;
}

/**
 * Create basic simplified results when AI insights aren't available
 */
function createBasicSimplifiedResults(_modules: Record<string, unknown>): SimplifiedResults {
  return {
    score: {
      riskLevel: "info",
      assessment: "Security scan completed. Detailed analysis is being processed.",
      findingCount: 0,
      urgentCount: 0,
    },
    priorityActions: [],
    categories: [],
    showRiskBasedView: false,
    totalFindings: 0,
    urgentCount: 0,
    distribution: { critical: 0, high: 0, medium: 0, low: 0, info: 0 },
  };
}

// ============================================================================
// UTILITY EXPORTS
// ============================================================================

export { CATEGORY_META, FINDING_TRANSLATIONS, calculateAdjustedDistribution, adjustFindingSeverity };
